import React, { useLayoutEffect, useRef, useState } from 'react'

import heroClientAgeas from '../../assets/images/hero/hero-ageas.png'
import heroClientEricsson from '../../assets/images/hero/hero-ericsson.png'
import heroClientNos from '../../assets/images/hero/hero-nos.png'
import heroClientPfizer from '../../assets/images/hero/hero-pfizer.png'
import heroClientWorten from '../../assets/images/hero/hero-worten.png'
import heroAppIcon from '../../assets/images/icons/hero-app-icon.png'
import heroDataIcon from '../../assets/images/icons/hero-data-icon.png'
import heroCloudIcon from '../../assets/images/icons/hero-cloud-icon.png'
import heroCiberIcon from '../../assets/images/icons/hero-ciber-icon.png'
import awardBestWorkplace from '../../assets/images/footer/SELO_MLPT2026.png'
import awardTop5 from '../../assets/images/footer/log-top5-2025.png'
import awardTop10 from '../../assets/images/footer/log-top10-2025.png'
import awardPme from '../../assets/images/hero/PME_Lider_2025_2D_cores.png'
import awardGoogle from '../../assets/images/footer/google-cloud.png'

import { Image } from '../Texts'
import AnimationLink from '../Link'
import LetsTalkCta from '../LetsTalkCta'
import HeroRotatingWord from './HeroRotatingWord'
import {
  HERO_ROTATE_INTERVAL_MS,
  HERO_ROTATE_WORDS_EN,
  HERO_ROTATE_WORDS_PT,
  HOME_PILLAR_SERVICE_LINES,
} from '../../constants/homeHero'

/** Matches SCSS `@include breakpoint("<=medium")` (max-width: 1190px) */
const MOBILE_HERO_MQ = '(max-width: 1190px)'

/** Must cover longest parallel transition (pillar lists + staggered lines); see _home.scss */
const HERO_GATE_LOCK_MS = 1320

/** One strong wheel tick (mouse / fast trackpad). */
const HERO_WHEEL_STRONG_PX = 10
/** Slow trackpad: sum small deltas until this total (each event is often < 10px). */
const HERO_WHEEL_ACCUM_PX = 14

/** Desktop open (expand): when wheel-down may start the transition — tuned for earlier start inside hero. */
const HERO_OPEN_MAX_SCROLL_VH = 0.72
const HERO_OPEN_RECT_BOTTOM_MIN_VH = 0.12
const HERO_OPEN_RECT_TOP_MAX_VH = 0.86
/** Block opening only in the last portion of the hero’s document height (keeps motion centered in hero). */
const HERO_OPEN_MAX_SCROLL_IN_SECTION = 0.78

/** Reverse (collapse): require meaningful hero visibility, shallow scroll, hero top near viewport top. */
const HERO_CLOSE_MIN_VISIBLE_RATIO = 0.38
/** Max scrollY (document) for reverse: offsetTop + this × viewport — stays in hero / upper fold. */
const HERO_CLOSE_SCROLL_MAX_VH = 0.55
/** Hero top vs viewport: allow only when the section is anchored in the upper band (not mid-page strip). */
const HERO_CLOSE_TOP_MAX_VH = 0.34
const HERO_CLOSE_TOP_MIN_VH = -0.22
/** Document-top band: below this scrollY, skip strict topVh (scroll cannot go below 0; no overscroll needed). */
const HERO_CLOSE_NEAR_DOC_TOP_VH = 0.06

function heroMeaningfullyInViewport(section) {
  if (!section) return false
  const rect = section.getBoundingClientRect()
  const vh = window.innerHeight
  const visibleTop = Math.max(0, rect.top)
  const visibleBottom = Math.min(vh, rect.bottom)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  if (visibleHeight <= 0) return false
  const sectionHeight = rect.height
  if (sectionHeight <= 0) return false
  const denom = Math.min(sectionHeight, vh)
  return visibleHeight / denom >= HERO_CLOSE_MIN_VISIBLE_RATIO
}

function heroCloseContextOk(section) {
  if (!section) return false
  const scrollY = window.scrollY || document.documentElement.scrollTop || 0
  const vh = window.innerHeight
  const rect = section.getBoundingClientRect()
  const offsetTop = section.offsetTop || 0
  const scrollCap = offsetTop + vh * HERO_CLOSE_SCROLL_MAX_VH
  if (scrollY > scrollCap) return false
  if (scrollY <= vh * HERO_CLOSE_NEAR_DOC_TOP_VH) {
    return heroMeaningfullyInViewport(section)
  }
  const topVh = rect.top / vh
  if (topVh > HERO_CLOSE_TOP_MAX_VH || topVh < HERO_CLOSE_TOP_MIN_VH) return false
  return heroMeaningfullyInViewport(section)
}

const HomeOne = ({ lang }) => {
  const isEn = lang === 'en'
  const rotateWords = isEn ? HERO_ROTATE_WORDS_EN : HERO_ROTATE_WORDS_PT
  const [isMobileLayout, setIsMobileLayout] = useState(false)
  /** Desktop only: idle ⇄ open with guided locks during opening | closing */
  const [heroDesktopPhase, setHeroDesktopPhase] = useState('idle')
  const sectionRef = useRef(null)
  const heroWheelOpenAccumRef = useRef(0)
  const heroWheelCloseAccumRef = useRef(0)

  const pillarBlocks = [
    {
      key: 'app-dev',
      label: isEn
        ? 'Application Development & Modernization'
        : 'Desenvolvimento e Modernização de Aplicações',
      to: isEn
        ? '/en/application-development-modernization/'
        : '/desenvolvimento-modernizacao-aplicacoes/',
      icon: heroAppIcon,
    },
    {
      key: 'data-ai',
      label: isEn ? 'Data & AI' : 'Dados & IA',
      to: isEn ? '/en/data-ai/' : '/dados-ia/',
      icon: heroDataIcon,
    },
    {
      key: 'cloud',
      label: isEn ? 'Cloud & Infrastructure' : 'Cloud & Infraestrutura',
      to: isEn ? '/en/cloud-infrastructure/' : '/cloud-infraestrutura/',
      icon: heroCloudIcon,
    },
    {
      key: 'cyber',
      label: isEn ? 'Cyber Security' : 'Cibersegurança',
      to: isEn ? '/en/cyber-security/' : '/ciberseguranca/',
      icon: heroCiberIcon,
    },
  ]

  const clientLogos = [
    { image: heroClientEricsson, title: 'ERICSSON', width: 63, height: 55 },
    { image: heroClientAgeas, title: 'AGEAS', width: 102, height: 41 },
    { image: heroClientWorten, title: 'WORTEN', width: 102, height: 22 },
    { image: heroClientPfizer, title: 'PFIZER', width: 102, height: 42 },
    { image: heroClientNos, title: 'NOS', width: 85, height: 47 },
  ]

  /* Intrinsic dimensions for layout box; same Image pattern as clients. */
  const awardLogos = [
    {
      image: awardBestWorkplace,
      title: isEn ? 'Best Workplaces' : 'Melhores empresas para trabalhar',
      width: 1528,
      height: 1439,
    },
    {
      image: awardTop5,
      title: isEn ? 'Top 5% scoring' : 'Top 5%',
      width: 1528,
      height: 1529,
    },
    {
      image: awardTop10,
      title: isEn ? 'Top 10' : 'Top 10',
      width: 1527,
      height: 1529,
    },
    {
      image: awardPme,
      title: 'PME Líder 2025',
      width: 58,
      height: 58,
    },
    {
      image: awardGoogle,
      title: isEn ? 'Google Cloud Partner' : 'Parceiro Google Cloud',
      width: 724,
      height: 720,
    },
  ]

  const trustedLabel = isEn ? 'Trusted by' : 'Confiam em nós'
  const proudLabel = isEn ? 'Proud of' : 'Orgulho em'

  const screenReaderTitle = activeIndex => {
    const w = rotateWords[activeIndex]
    return isEn ? `${w} Tech Solutions` : `Soluções Tecnológicas ${w}`
  }

  const pillarLines = key => {
    const block = HOME_PILLAR_SERVICE_LINES[key]
    if (!block) return []
    return isEn ? block.en : block.pt
  }

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    const mq = window.matchMedia(MOBILE_HERO_MQ)
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    const syncLayoutMode = () => {
      const mobile = mq.matches
      setIsMobileLayout(mobile)
      if (mobile) {
        setHeroDesktopPhase('idle')
        return
      }
      if (reduceMotion.matches) {
        setHeroDesktopPhase('open')
      }
    }

    syncLayoutMode()

    const onMq = () => syncLayoutMode()
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onMq)
    } else {
      mq.addListener(onMq)
    }

    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onMq)
      } else {
        mq.removeListener(onMq)
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || isMobileLayout) {
      const root = sectionRef.current
      if (root) root.style.removeProperty('--hero-intro-h')
      return
    }
    if (heroDesktopPhase !== 'idle') return

    const root = sectionRef.current
    if (!root) return

    const measureIntro = () => {
      const intro = root.querySelector('.home-hero__intro')
      if (!intro) return
      const h = Math.ceil(intro.scrollHeight)
      if (h > 0) root.style.setProperty('--hero-intro-h', `${h}px`)
    }

    measureIntro()
    window.addEventListener('resize', measureIntro, { passive: true })
    return () => window.removeEventListener('resize', measureIntro)
  }, [isMobileLayout, heroDesktopPhase])

  useLayoutEffect(() => {
    heroWheelOpenAccumRef.current = 0
    heroWheelCloseAccumRef.current = 0
  }, [heroDesktopPhase])

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || isMobileLayout) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (reduceMotion.matches) return

    const phase = heroDesktopPhase

    const readOpenIntent = deltaY => {
      if (deltaY <= 0) {
        heroWheelOpenAccumRef.current = 0
        return false
      }
      if (deltaY >= HERO_WHEEL_STRONG_PX) {
        heroWheelOpenAccumRef.current = 0
        return true
      }
      heroWheelOpenAccumRef.current += deltaY
      if (heroWheelOpenAccumRef.current >= HERO_WHEEL_ACCUM_PX) {
        heroWheelOpenAccumRef.current = 0
        return true
      }
      return false
    }

    const readCloseIntent = deltaY => {
      if (deltaY >= 0) {
        heroWheelCloseAccumRef.current = 0
        return false
      }
      const d = -deltaY
      if (d >= HERO_WHEEL_STRONG_PX) {
        heroWheelCloseAccumRef.current = 0
        return true
      }
      heroWheelCloseAccumRef.current += d
      if (heroWheelCloseAccumRef.current >= HERO_WHEEL_ACCUM_PX) {
        heroWheelCloseAccumRef.current = 0
        return true
      }
      return false
    }

    const heroOpenTriggerOk = () => {
      const section = sectionRef.current
      if (!section) return false
      const y = window.scrollY || document.documentElement.scrollTop || 0
      const rect = section.getBoundingClientRect()
      const vh = window.innerHeight
      const offsetTop = section.offsetTop || 0
      const secH = section.offsetHeight || 1
      if (y > vh * HERO_OPEN_MAX_SCROLL_VH) return false
      if (rect.bottom < vh * HERO_OPEN_RECT_BOTTOM_MIN_VH) return false
      if (rect.top > vh * HERO_OPEN_RECT_TOP_MAX_VH) return false
      if (y > offsetTop + secH * HERO_OPEN_MAX_SCROLL_IN_SECTION) return false
      return true
    }

    const onWheel = e => {
      if (e.ctrlKey) return
      const section = sectionRef.current
      if (!section) return

      const dy = e.deltaY
      const openingIntent = readOpenIntent(dy)
      const closingIntent = readCloseIntent(dy)
      if (!openingIntent && !closingIntent) return

      if (phase === 'opening') {
        if (closingIntent) {
          e.preventDefault()
          setHeroDesktopPhase('closing')
        }
        return
      }
      if (phase === 'closing') {
        if (openingIntent) {
          e.preventDefault()
          setHeroDesktopPhase('opening')
        }
        return
      }
      if (phase === 'idle') {
        if (openingIntent && heroOpenTriggerOk()) {
          e.preventDefault()
          setHeroDesktopPhase('opening')
        }
        return
      }
      if (phase === 'open') {
        if (closingIntent && heroCloseContextOk(section)) {
          e.preventDefault()
          setHeroDesktopPhase('closing')
        }
      }
    }

    window.addEventListener('wheel', onWheel, { passive: false })
    return () => window.removeEventListener('wheel', onWheel)
  }, [isMobileLayout, heroDesktopPhase])

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return
    if (isMobileLayout) return
    if (heroDesktopPhase !== 'opening' && heroDesktopPhase !== 'closing') return

    const nextPhase = heroDesktopPhase === 'opening' ? 'open' : 'idle'
    const t = window.setTimeout(() => {
      setHeroDesktopPhase(nextPhase)
    }, HERO_GATE_LOCK_MS)

    return () => {
      window.clearTimeout(t)
    }
  }, [isMobileLayout, heroDesktopPhase])

  const desktopExpanded =
    !isMobileLayout && (heroDesktopPhase === 'opening' || heroDesktopPhase === 'open')

  const heroStateClass = isMobileLayout
    ? 'home-hero--collapsed'
    : `home-hero--collapsed${desktopExpanded ? ' home-hero--expanded' : ''}${
        heroDesktopPhase === 'closing' ? ' home-hero--gate-closing' : ''
      }`

  return (
    <section
      ref={sectionRef}
      id="home-one"
      className={`home-hero ${heroStateClass} content container`}
    >
      <div className="home-hero__inner">
        <div className="home-hero__intro">
          <HeroRotatingWord words={rotateWords} intervalMs={HERO_ROTATE_INTERVAL_MS}>
            {({ activeIndex, words }) => (
              <h1 className="home-hero__title">
                <span className="home-hero__sr-only" aria-live="polite" aria-atomic="true">
                  {screenReaderTitle(activeIndex)}
                </span>
                <span className="home-hero__title-visual" aria-hidden="true">
                  {isEn ? (
                    <>
                      <span
                        className="home-hero__rotating home-hero__rotating--en"
                      >
                        {words.map((word, i) => (
                          <span key={word} className={i === activeIndex ? 'is-active' : ''}>
                            {word}
                          </span>
                        ))}
                      </span>{' '}
                      <span className="home-hero__title-rest">Tech Solutions</span>
                    </>
                  ) : (
                    <>
                      <span className="home-hero__title-rest">Soluções Tecnológicas </span>
                      <span className="home-hero__rotating">
                        {words.map((word, i) => (
                          <span key={word} className={i === activeIndex ? 'is-active' : ''}>
                            {word}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                </span>
              </h1>
            )}
          </HeroRotatingWord>

          <p className="home-hero__subtitle">
            {isEn
              ? 'An agile software development company'
              : 'Uma empresa de desenvolvimento de software ágil.'}
          </p>
        </div>

        <div className="home-hero__motion">
          <div className="home-hero__pillars">
            {pillarBlocks.map(p => (
              <AnimationLink
                key={p.key}
                className="home-hero__pillar-link"
                hex="#322EFF"
                paintDrip
                to={p.to}
              >
                <div className="home-hero__pillar-title">
                  <p className="home-hero__pillar-label">{p.label}</p>
                </div>
                <div className="home-hero__pillar-icon-row">
                  <span className="home-hero__pillar-icon" aria-hidden="true">
                    <img
                      src={p.icon}
                      alt=""
                      width={56}
                      height={56}
                      loading="eager"
                      decoding="async"
                    />
                  </span>
                </div>
                <ul className="home-hero__pillar-list" aria-label={p.label}>
                  {pillarLines(p.key).map(line => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </AnimationLink>
            ))}
          </div>

          <div className="home-hero__cta-wrap">
            <LetsTalkCta isEn={isEn} hex="#322EFF" tone="accent" paintDrip />
          </div>

          <div
            className="home-hero__logos"
            role="region"
            aria-label={isEn ? 'Partners and awards' : 'Parceiros e distinções'}
          >
            <div className="home-hero__logos-row">
              <div className="home-hero__logos-group home-hero__logos-group--clients">
                <p className="home-hero__logos-label">{trustedLabel}</p>
                <ul className="home-hero__logos-list home-hero__logos-list--clients">
                  {clientLogos.map(c => (
                    <li key={c.title}>
                      <Image {...c} />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="home-hero__logos-group home-hero__logos-group--awards">
                <p className="home-hero__logos-label">{proudLabel}</p>
                <ul className="home-hero__logos-list home-hero__logos-list--awards">
                  {awardLogos.map((a, i) => (
                    <li key={`hero-award-${i}`}>
                      <Image {...a} loading="eager" decoding="async" />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HomeOne
